"use client";

import Link from "next/link";
import { useState } from "react";
import { GetAllEventsOutput } from "~/types/EventTypesInferred";
import { cn } from "~/utils/fontsConfig";
import { If } from "../Conditional";
import { Button } from "../themed/Button";
import Modal from "../themed/Modal";
import AttendeesList from "../users-components/AttendeesList";
import { EventDetailsTag } from "./EventDetailsTag";
import { EventImage } from "./EventImage";
import { EventImageBadgeOverlay } from "./EventImageOverlay";
import { PrivateTag } from "./PrivateTag";
import { AttendanceStatus } from "@prisma/client";
import { dedupe } from "~/utils/various";
import {
    AllTailwindFontTypes,
    CustomizableLayout,
} from "~/types/CustomizableLayout";

interface CardCustomization extends CustomizableLayout {
    hideCommunityName?: boolean;
    hideCity?: boolean;
    eventNameFont?: AllTailwindFontTypes;
    rounded?: "rounded-xl" | "rounded-none";
    dateTagClassName?: string;
}

const defaultCustomization: CardCustomization = {
    hideCommunityName: false,
    hideCity: false,
    textFont: "font-faktum",
    eventNameFont: "font-faktum",
    rounded: "rounded-xl",
};

export const PossiblyExternalLink = ({
    href,
    riverUrl,
    className,
    children,
    permissions,
    customization,
}: {
    href: string;
    riverUrl: string;
    className?: string;
    children: React.ReactNode;
    permissions?: {
        isAdmin?: boolean;
        isManager?: boolean;
        isHost?: boolean;
    };
    customization?: CardCustomization;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const hasEditPermission =
        permissions?.isAdmin || permissions?.isManager || permissions?.isHost;

    if (href.startsWith("https://") && hasEditPermission) {
        return (
            <>
                <span
                    onClick={() => setIsOpen(true)}
                    className={cn("cursor-pointer", className)}
                >
                    {children}
                </span>
                {isOpen && (
                    <Modal
                        open={isOpen}
                        onOpenChange={() => setIsOpen(false)}
                        title="External link"
                        description="This event is hosted on an external platform, would you like to access it?"
                        footer={
                            <span className="flex gap-2">
                                <Link href={riverUrl}>
                                    <Button
                                        variant="secondary"
                                        className="w-[140px]"
                                    >
                                        See on River
                                    </Button>
                                </Link>
                                <Link
                                    href={href}
                                    target="_blank"
                                    className="w-[140px]"
                                >
                                    <Button>Open in new tab</Button>
                                </Link>
                            </span>
                        }
                    ></Modal>
                )}
            </>
        );
    }
    if (!hasEditPermission && href.startsWith("https://")) {
        return (
            <Link href={href} className={className} target="_blank">
                {children}
            </Link>
        );
    }
    return (
        <Link href={href} className={className}>
            {children}
        </Link>
    );
};

export const EventCard = ({
    event,
    className,
    hideAttendees,
    customization = defaultCustomization,
    whiteLabelSlug,
}: {
    event: GetAllEventsOutput;
    className?: string;
    hideAttendees?: boolean;
    customization?: CardCustomization;
    whiteLabelSlug?: string;
}) => {
    if (!event) return null;

    let eventLink = `/events/${event.slug}`;

    if (whiteLabelSlug) {
        eventLink = `/beta/${whiteLabelSlug}/events/${event.slug}`;
    } else if (event.isExternalEvent && event.externalEventUrl) {
        eventLink = event.externalEventUrl;
    }

    // TODO refactor and generalize that
    const isParallax =
        event.image &&
        event.communityName === "one" &&
        event.tags.includes("parallax");

    const showCNameAndCity =
        !!event.city ||
        (!!event.community?.name && !customization?.hideCommunityName);

    return (
        <div
            id={`event-card-${event.id}`}
            className={cn(
                "relative mx-auto flex w-full flex-col gap-2 md:mx-0 md:w-auto",
                className,
                customization?.className,
            )}
        >
            <PossiblyExternalLink
                href={eventLink}
                riverUrl={`/events/${event.slug}`}
                permissions={{ isHost: event.isHost }}
            >
                <EventDetailsTag
                    event={event}
                    className={customization?.dateTagClassName}
                />
                {isParallax && (
                    <EventImage
                        isReadonly={true}
                        className={cn(
                            "aspect-video",
                            customization?.rounded ?? "rounded-xl",
                        )}
                        event={event}
                        options={{ type: "contain" }}
                    />
                )}
                {event.image && !isParallax && (
                    <div
                        className={cn(
                            "relative overflow-hidden",
                            customization?.rounded ?? "rounded-xl",
                        )}
                    >
                        <EventImage
                            isReadonly={true}
                            event={event}
                            options={{ type: "contain" }}
                            allowAnimations={true}
                            className={cn(
                                customization?.rounded ?? "rounded-xl",
                            )}
                        />
                        <EventImageBadgeOverlay
                            badges={event.eventBadges}
                            size="large"
                        />

                        <PrivateTag event={event} />
                    </div>
                )}
            </PossiblyExternalLink>
            <If condition={showCNameAndCity}>
                <div className="flex items-center justify-between gap-2 text-center">
                    <div className="flex items-center gap-2 text-center text-sm">
                        {event.community?.name &&
                            !customization?.hideCommunityName && (
                                <Link href={`/${event.community?.slug}`}>
                                    <h6 className="select-none text-sm hover:cursor-pointer hover:text-v2-cta">
                                        {event.community?.name}
                                    </h6>
                                </Link>
                            )}
                        {event.city && !customization?.hideCity && (
                            <>
                                • <h6 className="text-sm">{event.city}</h6>
                            </>
                        )}

                        {event.virtualEventUrl && (
                            <>
                                • <h6 className="text-sm">Virtual Event</h6>
                            </>
                        )}
                    </div>
                </div>
            </If>
            <PossiblyExternalLink
                href={eventLink}
                riverUrl={`/events/${event.slug}`}
            >
                <div
                    className={cn(
                        "text-[20px] font-[500]",
                        customization?.eventNameFont,
                    )}
                >
                    {event.name}
                </div>
            </PossiblyExternalLink>
            <If condition={!hideAttendees}>
                <AttendeesList
                    attendees={dedupe(
                        [
                            ...event.hosts.map((h) => ({
                                ...h,
                                status: AttendanceStatus.ATTENDING,
                            })),
                            ...event.attendees,
                        ],
                        "user.id",
                    )}
                    showLabel={false}
                    iconSize={30}
                    showMax={Math.round(280 / 30)}
                    spaceIcons={false}
                />
            </If>
        </div>
    );
};
